import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, SlidersHorizontal, X, Search } from 'lucide-react'
import { ModernInput } from '../ui'
import { AutocompleteInput, AutocompleteOption } from '../ui'
import { cn } from '../../lib/utils'

interface LeadOptions {
  statuses: Array<{ value: string; label: string }>
  sources: Array<{ value: string; label: string }>
  funnelStages: Array<{ value: string; label: string }>
}

interface LeadFiltersProps {
  nameFilter: string
  phoneFilter: string
  emailFilter: string
  documentFilter: string
  statusFilter: string
  sourceFilter: string
  funnelStageFilter: string
  assignedTo: string
  assignedToLabel: string
  vehicleInterest: string
  vehicleSearchTerm: string
  createdAtFrom: string
  createdAtTo: string
  hasProposalFilter: string
  isConvertedFilter: string

  // Options from API
  leadOptions: LeadOptions
  userOptions: AutocompleteOption[]
  isUsersLoading: boolean

  // Vehicle options for autocomplete
  vehicleOptions: AutocompleteOption[]
  isVehiclesLoading: boolean
  onNameChange: (value: string) => void
  onPhoneChange: (value: string) => void
  onEmailChange: (value: string) => void
  onDocumentChange: (value: string) => void
  onStatusChange: (value: string) => void
  onSourceChange: (value: string) => void
  onFunnelStageChange: (value: string) => void
  onAssignedToChange: (option: AutocompleteOption) => void
  onAssignedToLabelChange: (value: string) => void
  onVehicleInterestChange: (option: AutocompleteOption) => void
  onVehicleSearchTermChange: (value: string) => void
  onCreatedAtFromChange: (value: string) => void
  onCreatedAtToChange: (value: string) => void
  onHasProposalChange: (value: string) => void
  onIsConvertedChange: (value: string) => void
  onClearFilters: () => void
  activeCount: number
}

interface FilterSectionProps {
  title: string
  children: React.ReactNode
  defaultOpen?: boolean
}

const FilterSection: React.FC<FilterSectionProps> = ({
  title,
  children,
  defaultOpen = true,
}) => {
  const [open, setOpen] = useState(defaultOpen)

  return (
    <div className="border-b border-border last:border-b-0">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-2.5 text-xs font-semibold uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors"
      >
        {title}
        <ChevronDown
          className={cn(
            'h-3.5 w-3.5 transition-transform duration-200',
            open && 'rotate-180',
          )}
        />
      </button>
      {open && <div className="px-4 pb-4 space-y-3">{children}</div>}
    </div>
  )
}

const TriStateSelect: React.FC<{
  label: string
  value: string
  onChange: (value: string) => void
  yesLabel: string
  noLabel: string
  allLabel: string
}> = ({ label, value, onChange, yesLabel, noLabel, allLabel }) => (
  <div className="space-y-1.5">
    <label className="text-[0.625rem] font-semibold uppercase tracking-widest text-muted-foreground block">
      {label}
    </label>
    <div className="grid grid-cols-3 gap-1.5">
      <button
        type="button"
        onClick={() => onChange('all')}
        className={cn(
          'text-xs font-medium px-2 py-1.5 rounded-md border transition-all',
          value === 'all'
            ? 'bg-accent text-white border-accent'
            : 'bg-card text-foreground border-border hover:border-accent/40 hover:bg-surface',
        )}
      >
        {allLabel}
      </button>
      <button
        type="button"
        onClick={() => onChange('true')}
        className={cn(
          'text-xs font-medium px-2 py-1.5 rounded-md border transition-all',
          value === 'true'
            ? 'bg-accent text-white border-accent'
            : 'bg-card text-foreground border-border hover:border-accent/40 hover:bg-surface',
        )}
      >
        {yesLabel}
      </button>
      <button
        type="button"
        onClick={() => onChange('false')}
        className={cn(
          'text-xs font-medium px-2 py-1.5 rounded-md border transition-all',
          value === 'false'
            ? 'bg-accent text-white border-accent'
            : 'bg-card text-foreground border-border hover:border-accent/40 hover:bg-surface',
        )}
      >
        {noLabel}
      </button>
    </div>
  </div>
)

export const LeadFilters: React.FC<LeadFiltersProps> = ({
  nameFilter,
  phoneFilter,
  emailFilter,
  documentFilter,
  statusFilter,
  sourceFilter,
  assignedTo,
  assignedToLabel,
  vehicleInterest,
  vehicleSearchTerm,
  createdAtFrom,
  createdAtTo,
  isConvertedFilter,
  leadOptions,
  userOptions,
  isUsersLoading,
  vehicleOptions,
  isVehiclesLoading,
  onNameChange,
  onPhoneChange,
  onEmailChange,
  onDocumentChange,
  onStatusChange,
  onSourceChange,
  onAssignedToChange,
  onAssignedToLabelChange,
  onVehicleInterestChange,
  onVehicleSearchTermChange,
  onCreatedAtFromChange,
  onCreatedAtToChange,
  onIsConvertedChange,
  onClearFilters,
  activeCount,
}) => {
  const { t } = useTranslation()

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '')
    let masked = ''
    if (digits.length > 0) masked = `(${digits.slice(0, 2)}`
    if (digits.length >= 3) masked += `) ${digits.slice(2, 7)}`
    if (digits.length >= 7) masked += `-${digits.slice(7, 11)}`
    onPhoneChange(masked)
  }

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden shadow-precision">
      <div className="flex items-center justify-between px-4 py-3 bg-surface border-b border-border">
        <div className="flex items-center gap-2">
          <SlidersHorizontal className="h-4 w-4 text-accent" />
          <span className="text-sm font-semibold text-foreground">Filtros</span>
          {activeCount > 0 && (
            <span className="inline-flex items-center justify-center h-4 min-w-4 px-1 text-[10px] font-bold rounded-full bg-accent text-white">
              {activeCount}
            </span>
          )}
        </div>
        {activeCount > 0 && (
          <button
            type="button"
            onClick={onClearFilters}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-accent transition-colors"
          >
            <X className="h-3 w-3" />
            {t('leads.filters.clearFilters')}
          </button>
        )}
      </div>

      <FilterSection title={t('leads.filters.serviceUser')}>
        <AutocompleteInput
          value={assignedToLabel}
          selectedId={assignedTo}
          options={userOptions}
          loading={isUsersLoading}
          placeholder={t('leads.filters.serviceUserPlaceholder')}
          emptyMessage={t('common.noResults')}
          onChange={onAssignedToLabelChange}
          onSelect={onAssignedToChange}
        />
      </FilterSection>

      <FilterSection title={t('leads.filters.status')}>
        <div className="grid grid-cols-2 gap-1.5">
          <button
            type="button"
            onClick={() => onStatusChange('all')}
            className={cn(
              'text-xs font-medium px-2 py-1.5 rounded-md border transition-all',
              statusFilter === 'all'
                ? 'bg-accent text-white border-accent'
                : 'bg-card text-foreground border-border hover:border-accent/40 hover:bg-surface',
            )}
          >
            {t('leads.filters.all')}
          </button>
          {leadOptions.statuses.map((s) => (
            <button
              key={s.value}
              type="button"
              onClick={() => onStatusChange(s.value)}
              className={cn(
                'text-xs font-medium px-2 py-1.5 rounded-md border transition-all',
                statusFilter === s.value
                  ? 'bg-accent text-white border-accent'
                  : 'bg-card text-foreground border-border hover:border-accent/40 hover:bg-surface',
              )}
            >
              {s.label}
            </button>
          ))}
        </div>
      </FilterSection>

      <FilterSection title={t('leads.filters.source')} defaultOpen={false}>
        <div className="space-y-1">
          <button
            type="button"
            onClick={() => onSourceChange('all')}
            className={cn(
              'w-full text-left text-xs font-medium px-3 py-2 rounded-md border transition-all',
              sourceFilter === 'all'
                ? 'bg-accent/10 text-accent border-accent/30'
                : 'bg-card text-foreground border-transparent hover:bg-surface hover:border-border',
            )}
          >
            {t('leads.filters.all')}
          </button>
          {leadOptions.sources.map((s) => (
            <button
              key={s.value}
              type="button"
              onClick={() => onSourceChange(s.value)}
              className={cn(
                'w-full text-left text-xs font-medium px-3 py-2 rounded-md border transition-all',
                sourceFilter === s.value
                  ? 'bg-accent/10 text-accent border-accent/30'
                  : 'bg-card text-foreground border-transparent hover:bg-surface hover:border-border',
              )}
            >
              {s.label}
            </button>
          ))}
        </div>
      </FilterSection>

      <FilterSection title={t('leads.filters.vehicleInterest')}>
        <AutocompleteInput
          value={vehicleSearchTerm}
          selectedId={vehicleInterest}
          options={vehicleOptions}
          loading={isVehiclesLoading}
          placeholder={t('leads.filters.vehicleInterestPlaceholder')}
          emptyMessage={t('common.noResults')}
          onChange={onVehicleSearchTermChange}
          onSelect={onVehicleInterestChange}
          leftIcon={<Search className="h-4 w-4 text-muted-foreground" />}
        />
      </FilterSection>

      <FilterSection title={t('leads.filters.customerData')} defaultOpen={false}>
        <div>
          <label className="text-[0.625rem] font-semibold uppercase tracking-widest text-muted-foreground block mb-1">
            {t('leads.filters.name')}
          </label>
          <ModernInput
            type="text"
            placeholder={t('leads.filters.namePlaceholder')}
            value={nameFilter}
            onChange={(e) => onNameChange(e.target.value)}
            className="w-full px-3 py-1.5 text-xs bg-background border-border rounded-md text-foreground placeholder:text-muted-foreground"
          />
        </div>
        <div>
          <label className="text-[0.625rem] font-semibold uppercase tracking-widest text-muted-foreground block mb-1">
            {t('leads.filters.phone')}
          </label>
          <ModernInput
            type="text"
            placeholder={t('leads.filters.phonePlaceholder')}
            value={phoneFilter}
            onChange={handlePhoneChange}
            className="w-full px-3 py-1.5 text-xs bg-background border-border rounded-md text-foreground placeholder:text-muted-foreground"
          />
        </div>
        <div>
          <label className="text-[0.625rem] font-semibold uppercase tracking-widest text-muted-foreground block mb-1">
            {t('leads.filters.email')}
          </label>
          <ModernInput
            type="text"
            placeholder={t('leads.filters.emailPlaceholder')}
            value={emailFilter}
            onChange={(e) => onEmailChange(e.target.value)}
            className="w-full px-3 py-1.5 text-xs bg-background border-border rounded-md text-foreground placeholder:text-muted-foreground"
          />
        </div>
        <div>
          <label className="text-[0.625rem] font-semibold uppercase tracking-widest text-muted-foreground block mb-1">
            {t('leads.filters.document')}
          </label>
          <ModernInput
            type="text"
            placeholder={t('leads.filters.documentPlaceholder')}
            value={documentFilter}
            onChange={(e) => onDocumentChange(e.target.value)}
            className="w-full px-3 py-1.5 text-xs bg-background border-border rounded-md text-foreground placeholder:text-muted-foreground"
          />
        </div>
</FilterSection>

      <FilterSection title={t('leads.filters.createdAt')} defaultOpen={false}>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label htmlFor="createdAtFrom" className="text-[0.625rem] font-semibold uppercase tracking-widest text-muted-foreground block mb-1">
              {t('leads.filters.createdAtFrom')}
            </label>
            <div className="relative">
              <ModernInput
                id="createdAtFrom"
                type="date"
                value={createdAtFrom}
                onChange={(e) => onCreatedAtFromChange(e.target.value)}
                className="w-full px-3 py-1.5 text-xs bg-background border-border rounded-md text-foreground pr-8"
              />
              {createdAtFrom && (
                <button
                  type="button"
                  onClick={() => onCreatedAtFromChange('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-surface rounded transition-colors"
                  title={t('leads.filters.clear')}
                >
                  <X className="h-3 w-3 text-muted-foreground" />
                </button>
              )}
            </div>
          </div>
          <div>
            <label htmlFor="createdAtTo" className="text-[0.625rem] font-semibold uppercase tracking-widest text-muted-foreground block mb-1">
              {t('leads.filters.createdAtTo')}
            </label>
            <div className="relative">
              <ModernInput
                id="createdAtTo"
                type="date"
                value={createdAtTo}
                onChange={(e) => onCreatedAtToChange(e.target.value)}
                className="w-full px-3 py-1.5 text-xs bg-background border-border rounded-md text-foreground pr-8"
              />
              {createdAtTo && (
                <button
                  type="button"
                  onClick={() => onCreatedAtToChange('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-surface rounded transition-colors"
                  title={t('leads.filters.clear')}
                >
                  <X className="h-3 w-3 text-muted-foreground" />
                </button>
              )}
            </div>
          </div>
        </div>
      </FilterSection>

      {/* Proposta e Conversão */}
<FilterSection title={t('leads.filters.proposalConversion')} defaultOpen={false}>
        <TriStateSelect
          label={t('leads.filters.converted')}
          value={isConvertedFilter}
          onChange={onIsConvertedChange}
          yesLabel={t('leads.filters.yes')}
          noLabel={t('leads.filters.no')}
          allLabel={t('leads.filters.all')}
        />
      </FilterSection>
    </div>
  )
}